import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useApiCreateStudentSurveyPublicAnswer, useApiReadStudentSurveyPublic} from "@/api/survey";
import {toast} from "react-toastify";
import ApiHandleSuccessMessage, {ApiHandleErrorMessage} from "@/lib/helper/apiHandleResultMessage";
import {URLS} from "@/lib/urls";
import StudentLayout from "@/components/layout/student/studentLayout";
import toFaNumber from "@/lib/utils/number";
import {Input} from "antd";

function StudentLoadPublicSurvey(props) {
    const param = useParams();
    const id = param.id;
    const [answers, setAnswers] = useState({});
    const navigate = useNavigate();
    const [notifNoCert, setNotifNoCert] = useState(false)

    const {data, isLoading} = useApiReadStudentSurveyPublic(id);

    const {mutate: submitSurveyPublic, isPending: isSubmitting} = useApiCreateStudentSurveyPublicAnswer();

    const handleAnswerChange = (questionId, value, teacherId = null) => {
        const key = teacherId ? `${questionId}_${teacherId}` : questionId;
        setAnswers(prev => ({
            ...prev,
            [key]: value
        }));
    };

    useEffect(() => {
        if (data?.data?.data?.length === 0 && !notifNoCert) {
            setNotifNoCert(true)
            toast.info('در حال حاضر نظرسنجی فعال نیست')
            navigate(-1);

        }
    }, [data, notifNoCert]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate required questions
        const requiredQuestions = data?.data?.data?.questions?.filter(q => q.required);
        const missingAnswers = requiredQuestions?.filter(q => {
            if (q.for_teacher) {
                // For teacher questions, check if all teachers have answers
                return q.lessons?.some(lesson => !answers[`${q.id}_${lesson.teacher_detail.id}`]);
            }
            return !answers[q.id];
        });

        if (missingAnswers?.length > 0) {
            toast.error('لطفا به سوالات اجباری پاسخ دهید');
            return;
        }

        // Transform answers to match API expected format
        const transformedAnswers = data.data?.data?.questions?.reduce((acc, question) => {
            if (question.for_teacher && question.lessons) {
                // For teacher questions, create an entry for each teacher
                question.lessons.forEach(lesson => {
                    const answerKey = `${question.id}_${lesson.teacher_detail.id}`;
                    const answer = answers[answerKey];
                    if (answer) {
                        acc.push({
                            survey_id: question.survey_id,
                            question_id: question.id,
                            teacher_id: lesson.teacher_detail.id,
                            options: (Array.isArray(answer) ? answer : [answer]).map(i => parseInt(i)),
                            text: question.type === 'text' ? answer : null
                        });
                    }
                });
            } else {
                // For regular questions
                const answer = answers[question.id];
                if (answer) {
                    acc.push({
                        survey_id: question.survey_id,
                        question_id: question.id,
                        teacher_id: null,
                        options: (Array.isArray(answer) ? answer : [answer]).map(i => parseInt(i)),
                        text: question.type === 'text' ? answer : null
                    });
                }
            }
            return acc;
        }, []);

        submitSurveyPublic(
            {
                survey_id: id,
                data: {answers: transformedAnswers}
            },
            {
                onSuccess: (e) => {
                    ApiHandleSuccessMessage(e)
                    navigate(URLS.student.survey.publicSurvey)

                },
                onError: (error) => {
                    ApiHandleErrorMessage(error)

                }
            }
        );


    };

    if (isLoading) {
        return (
            <StudentLayout pageTitle={'نظرسنجی'} backTo={URLS.student.dashboard}>
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
            </StudentLayout>
        );
    }

    const renderQuestion = (question, index, teacherDetail = null) => {
        const questionId = teacherDetail ? `${question.id}_${teacherDetail.id}` : question.id;
        const questionTitle = teacherDetail
            ? `${question.title} (استاد ${teacherDetail.firstname} ${teacherDetail.lastname})`
            : question.title;

        return (
            <div key={questionId} className=" p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-2">
                    {toFaNumber(index)}. {' '}
                    {questionTitle}
                    {' '}
                    {question.required ? <small className={'mx-2 text-xs text-red-600'}>الزامی</small> : ''}
                </h3>
                {question.description && (
                    <p className=" mb-4">{question.description}</p>
                )}

                {question.type === 'single_choice' && (
                    <div className="space-y-2">
                        {question.options.map((option) => (
                            <label key={option.id} className="flex items-center space-x-2">
                                <input
                                    type="radio"
                                    name={`question_${questionId}`}
                                    value={option.id}
                                    onChange={(e) => handleAnswerChange(question.id, e.target.value, teacherDetail?.id)}
                                    required={question.required}
                                    className="form-radio"
                                />
                                <span className={'px-2'}>{option.title}</span>
                            </label>
                        ))}
                    </div>
                )}

                {question.type === 'multiple_choice' && (
                    <div className="space-y-2">
                        {question.options.map((option) => (
                            <label key={option.id} className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    value={option.id}
                                    onChange={(e) => {
                                        const currentAnswers = answers[questionId] || [];
                                        const newAnswers = e.target.checked
                                            ? [...currentAnswers, option.id]
                                            : currentAnswers.filter(id => id !== option.id);
                                        handleAnswerChange(question.id, newAnswers, teacherDetail?.id);
                                    }}
                                    required={question.required && (!answers[questionId] || answers[questionId].length === 0)}
                                    className="form-checkbox"
                                />
                                <span className={'px-2'}>{option.title}</span>
                            </label>
                        ))}
                    </div>
                )}

                {question.type === 'text' && (
                    <Input.TextArea
                        className="w-full p-2 border rounded"
                        rows="4"
                        onChange={(e) => handleAnswerChange(question.id, e.target.value, teacherDetail?.id)}
                        required={question.required}
                    />
                )}
            </div>
        );
    };

    const ElementAnswerToSurvey = <>
        <div className="max-w-3xl mx-auto p-4">
            <form onSubmit={handleSubmit} className="space-y-6">
                {data?.data?.data?.questions?.map((question, index) => {
                    if (question.for_teacher && question.lessons) {
                        // For teacher questions, render for each teacher
                        return question.lessons.map((lesson, lessonIndex) =>
                            renderQuestion(question, (index + 1) + '.' + (lessonIndex + 1), lesson.teacher_detail)
                        );
                    }
                    // For regular questions, render once
                    return renderQuestion(question, index + 1);
                })}

                <div className="flex justify-end">
                    <button

                        type="submit"
                        disabled={isSubmitting}
                        className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? 'در حال ثبت...' : 'ثبت پاسخ ها'}
                    </button>
                </div>

            </form>
        </div>
    </>

    return (
        <>
            <StudentLayout pageTitle={'نظرسنجی'} backTo={URLS.student.survey.publicSurvey}>
                {ElementAnswerToSurvey}
            </StudentLayout>

        </>
    );
}

export default StudentLoadPublicSurvey;